import { useCallback, useMemo, useState } from "react";
import { Activity, BellRing, BookOpenCheck, Building2, CalendarClock, Check, ChevronDown, Gauge, History, Plus, RefreshCw, Search, SlidersHorizontal, TrendingUp } from "lucide-react";
import { overagePolicyLabels, type HighRiskAction, type SessionView } from "@control-plane/contracts";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { PageHeader } from "../components/PageHeader";
import { SectionCard } from "../components/SectionCard";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { V3Select } from "../components/V3Select";
import { RemotePageState } from "../components/RemotePageState";
import { Wizard } from "../components/Wizard";
import { ApprovalGateModal, type ApprovalOperation } from "../components/ApprovalGate";
import { InlineFeedback } from "../components/Feedback";
import { StatusBadge } from "../components/StatusBadge";
import { V3PageTabs } from "../components/V3PageTabs";

type TenantOption = { id: string; name: string; status?: string };
type UsageItem = {
  meterId: string;
  displayName: string;
  unit: string;
  aggregation: "sum" | "max" | "last" | "unique_count";
  source: "control_plane" | "erp_runtime";
  decimalScale: number;
  billable: boolean;
  dataAvailable: boolean;
  includedQuantity: string | null;
  usedQuantity: string;
  remainingQuantity: string | null;
  todayQuantity: string;
  monthQuantity: string;
  unlimited: boolean;
  forecastExhaustedAt: string | null;
  dataLatencyMinutes: number;
  overagePolicy: keyof typeof overagePolicyLabels;
  periodStart: string;
  periodEnd: string;
  updatedAt: string;
};
type MeterCatalogItem = {
  id: string;
  meterKey: string;
  capabilityId: string | null;
  capabilityName: string | null;
  displayName: string;
  unit: string;
  aggregation: "sum" | "max" | "last" | "unique_count";
  source: "control_plane" | "erp_runtime";
  decimalScale: number;
  dedupeKeyRequired: boolean;
  billable: boolean;
  dataLatencyMinutes: number;
  lifecycle: "draft" | "active" | "retired";
  updatedAt: string;
};
type AlertPolicy = {
  id: string;
  meterId: string;
  meterName: string;
  thresholdPercent: number;
  channel: string;
  recipientKind: string;
  recipientRef: string;
  recipientLabel?: string | null;
  recipientName?: string | null;
  suppressionMinutes: number;
  status: "active" | "paused";
  updatedAt: string;
};
type UsageDashboard = {
  tenantId: string | null;
  tenantName: string;
  periodStart: string;
  periodEnd: string;
  updatedAt: string;
  items: UsageItem[];
  alertPolicies: AlertPolicy[];
  quotaOverrides: QuotaOverride[];
  adjustments: UsageAdjustment[];
};
type QuotaOverride = {
  id: string;
  meterId: string;
  meterName: string;
  unit: string;
  decimalScale: number;
  unlimited: boolean;
  limitQuantity: string | null;
  startsAt: string;
  endsAt: string;
  releasedAt: string | null;
  reason: string;
  approvalId?: string | null;
};
type UsageAdjustment = {
  id: string;
  meterId: string;
  meterName: string;
  unit: string;
  decimalScale: number;
  quantityDelta: string;
  periodStart: string;
  periodEnd: string | null;
  reason: string;
  createdAt: string;
  approvalId?: string | null;
};
type CapabilityOption = { id: string; displayName: string; domainLabel: string; lifecycle: string };
type UsagePageData = {
  tenants: TenantOption[];
  meters: MeterCatalogItem[];
  capabilities: CapabilityOption[];
  selectedTenantId: string;
  dashboard: UsageDashboard;
  alertPolicies: AlertPolicy[];
};

type DraftKind = "alert" | "adjust" | "override";
type DraftState = {
  kind: DraftKind;
  meterId: string;
  thresholdPercent: string;
  channel: string;
  recipientKind: "billing_profile" | "admin" | "tenant_contact";
  suppressionMinutes: string;
  quantityDelta: string;
  reason: string;
  startsAt: string;
  endsAt: string;
  limitQuantity: string;
};
type MeterDraftState = {
  meterKey: string;
  displayName: string;
  unit: string;
  aggregation: MeterCatalogItem["aggregation"];
  source: MeterCatalogItem["source"];
  decimalScale: string;
  dedupeKeyRequired: boolean;
  billable: boolean;
  dataLatencyMinutes: string;
  lifecycle: MeterCatalogItem["lifecycle"];
  capabilityId: string;
  reason: string;
};

const emptyDashboard = (tenantId: string, tenantName: string): UsageDashboard => ({
  tenantId,
  tenantName,
  periodStart: new Date().toISOString(),
  periodEnd: new Date(Date.now() + 30 * 86400_000).toISOString(),
  updatedAt: new Date().toISOString(),
  items: [],
  alertPolicies: [],
  quotaOverrides: [],
  adjustments: [],
});

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("zh-CN");
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString("zh-CN");
}

function formatQuantity(value: string | null | undefined, unit: string, decimalScale = 0) {
  if (value === null || value === undefined) return "不限量";
  const negative = value.startsWith("-");
  const digits = (negative ? value.slice(1) : value).replace(/^0+(?=\d)/, "");
  const padded = digits.padStart(decimalScale + 1, "0");
  const whole = decimalScale === 0 ? padded : padded.slice(0, -decimalScale);
  const fraction = decimalScale === 0 ? "" : padded.slice(-decimalScale).replace(/0+$/, "");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const quantity = `${negative ? "-" : ""}${grouped}${fraction ? `.${fraction}` : ""}`;
  return unit ? `${quantity} ${unit}` : quantity;
}

function formatLatency(minutes: number) {
  if (minutes <= 0) return "实时更新";
  if (minutes < 60) return `约延迟 ${minutes} 分钟`;
  return `约延迟 ${Math.round(minutes / 60)} 小时`;
}

function overagePolicyText(policy: keyof typeof overagePolicyLabels) {
  if (policy === "block_new") return "限制新增操作";
  if (policy === "suspend_feature") return "暂停对应功能";
  return overagePolicyLabels[policy] ?? "按套餐处理";
}

function aggregationText(value: MeterCatalogItem["aggregation"]) {
  return ({ sum: "累计相加", max: "取最高值", last: "取最新值", unique_count: "按业务记录去重计数" } as const)[value];
}

function sourceText(value: MeterCatalogItem["source"]) {
  return value === "erp_runtime" ? "租户 ERP 上报" : "总管后台记录";
}

function recipientText(value: AlertPolicy["recipientKind"]) {
  return ({ billing_profile: "账单联系人", tenant_contact: "企业主要联系人", admin: "平台值班管理员" } as Record<string, string>)[value] ?? "指定业务联系人";
}

function localDateTimeOffset(days: number) {
  const date = new Date(Date.now() + days * 86400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDateTimeLocal(value: string) {
  return new Date(value).toISOString();
}

function internalRequestKey(prefix: string) {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
}

function hasAction(session: SessionView | undefined, action: string) {
  return Boolean((session?.actions as readonly string[] | undefined)?.includes(action));
}

async function loadUsagePage(selectedTenantId: string, includeCapabilities: boolean): Promise<UsagePageData> {
  const [tenantsResponse, metersResponse, catalog] = await Promise.all([
    api<{ items: TenantOption[] }>("/api/tenants"),
    api<{ items: MeterCatalogItem[] }>("/api/usage/meters"),
    includeCapabilities
      ? api<{ capabilities: CapabilityOption[] }>("/api/commercial/catalog")
      : Promise.resolve({ capabilities: [] }),
  ]);
  const tenants = tenantsResponse.items;
  const effectiveTenant = tenants.find((tenant) => tenant.id === selectedTenantId) ?? tenants[0] ?? null;
  if (!effectiveTenant) {
    return {
      tenants,
      meters: metersResponse.items,
      capabilities: catalog.capabilities,
      selectedTenantId: "",
      dashboard: emptyDashboard("", "暂无企业"),
      alertPolicies: [],
    };
  }
  const query = `tenantId=${encodeURIComponent(effectiveTenant.id)}`;
  const dashboard = await api<UsageDashboard>(`/api/usage/dashboard?${query}`);
  return {
    tenants,
    meters: metersResponse.items,
    capabilities: catalog.capabilities,
    selectedTenantId: effectiveTenant.id,
    dashboard,
    alertPolicies: dashboard.alertPolicies,
  };
}

export function UsagePage({ session }: { session?: SessionView }) {
  const [selectedTenantId, setSelectedTenantId] = useState(() => new URLSearchParams(window.location.search).get("tenantId") ?? "");
  const [activeView, setActiveView] = useState<"usage" | "policy" | "records" | "meters">("usage");
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [meterDraft, setMeterDraft] = useState<MeterDraftState | null>(null);
  const [approval, setApproval] = useState<{ operation: ApprovalOperation; title: string; targetLabel: string; executePath: string; body: Record<string, unknown> } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const canReadCatalog = hasAction(session, "catalog.read");
  const loader = useCallback(() => loadUsagePage(selectedTenantId, canReadCatalog), [canReadCatalog, selectedTenantId]);
  const remote = useRemote(loader, (data) => data.tenants.length === 0);
  const data = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data : null;
  const selectedTenant = data?.tenants.find((tenant) => tenant.id === data.selectedTenantId) ?? null;
  const items = data?.dashboard.items ?? [];
  const canManageAlerts = hasAction(session, "usage.manage");
  const canManageMeters = hasAction(session, "usage.manage");
  const canAdjust = hasAction(session, "usage.adjust");
  const canOverride = hasAction(session, "overrides.manage");

  const meterOptions = useMemo(() => items.map((item) => ({ value: item.meterId, label: `${item.displayName}（${item.unit}）` })), [items]);
  const capabilityOptions = useMemo(() => {
    const active = (data?.capabilities ?? []).filter((item) => item.lifecycle === "active");
    const retained = meterDraft?.capabilityId && !active.some((item) => item.id === meterDraft.capabilityId)
      ? data?.meters.find((item) => item.capabilityId === meterDraft.capabilityId)
      : null;
    return [
      { value: "", label: "不绑定功能，只记录用量" },
      ...(retained ? [{ value: meterDraft!.capabilityId, label: retained.capabilityName ?? "保留现有功能关联", hint: "当前账号不可变更该关联" }] : []),
      ...active.map((item) => ({ value: item.id, label: item.displayName, hint: item.domainLabel })),
    ];
  }, [data?.capabilities, data?.meters, meterDraft?.capabilityId]);
  const periodText = data ? `${formatDate(data.dashboard.periodStart)} 至 ${formatDate(data.dashboard.periodEnd)}` : "—";
  const activePolicyCount = data?.alertPolicies.filter((item) => item.status === "active").length ?? 0;
  const activeOverrideCount = data?.dashboard.quotaOverrides.filter((item) => !item.releasedAt && new Date(item.startsAt) <= new Date() && new Date(item.endsAt) >= new Date()).length ?? 0;
  const blocked = remote.state.status === "error" && remote.state.httpStatus === 403
    ? { title: "当前账号还不能查看用量", description: "请让平台负责人开通用量查看权限，再回到本页刷新。" }
    : null;

  function openDraft(kind: DraftKind) {
    const firstMeter = items[0]?.meterId ?? "";
    setSubmitError(null);
    setDraft({
      kind,
      meterId: firstMeter,
      thresholdPercent: "80",
      channel: "internal",
      recipientKind: "billing_profile",
      suppressionMinutes: "1440",
      quantityDelta: "1",
      reason: "",
      startsAt: localDateTimeOffset(0),
      endsAt: localDateTimeOffset(7),
      limitQuantity: "100",
    });
  }

  function openMeterDraft(meter?: MeterCatalogItem) {
    setSubmitError(null);
    const random = globalThis.crypto?.randomUUID?.().replaceAll("-", "") ?? `${Date.now()}${Math.random().toString(16).slice(2)}`;
    setMeterDraft({
      meterKey: meter?.meterKey ?? `custom.${random}`,
      displayName: meter?.displayName ?? "",
      unit: meter?.unit ?? "次",
      aggregation: meter?.aggregation ?? "sum",
      source: meter?.source ?? "erp_runtime",
      decimalScale: String(meter?.decimalScale ?? 0),
      dedupeKeyRequired: meter?.dedupeKeyRequired ?? true,
      billable: meter?.billable ?? false,
      dataLatencyMinutes: String(meter?.dataLatencyMinutes ?? 15),
      lifecycle: meter?.lifecycle ?? "draft",
      capabilityId: meter?.capabilityId ?? "",
      reason: "",
    });
  }

  async function submitMeter() {
    if (!meterDraft) return;
    setSubmitError(null);
    try {
      await api("/api/usage/meters", { method: "POST", body: JSON.stringify({
        meterKey: meterDraft.meterKey,
        capabilityId: meterDraft.capabilityId || null,
        displayName: meterDraft.displayName.trim(),
        unit: meterDraft.unit.trim(),
        aggregation: meterDraft.aggregation,
        source: meterDraft.source,
        decimalScale: Number(meterDraft.decimalScale),
        dedupeKeyRequired: meterDraft.dedupeKeyRequired,
        billable: meterDraft.billable,
        dataLatencyMinutes: Number(meterDraft.dataLatencyMinutes),
        lifecycle: meterDraft.lifecycle,
        reason: meterDraft.reason.trim(),
        idempotencyKey: internalRequestKey("usage-meter"),
      }) });
      setMeterDraft(null);
      await remote.reload();
    } catch (failure) {
      setSubmitError(failure instanceof Error ? failure.message : "计量规则保存失败");
    }
  }

  async function submitAlert() {
    if (!draft || !data) return;
    setSubmitError(null);
    try {
      await api("/api/usage/alert-policies", { method: "POST", body: JSON.stringify({
        tenantId: data.selectedTenantId,
        meterId: draft.meterId,
        thresholdPercent: Number(draft.thresholdPercent),
        channel: draft.channel,
        recipientKind: draft.recipientKind,
        recipientRef: draft.recipientKind === "billing_profile" ? "billing-profile" : draft.recipientKind === "tenant_contact" ? "tenant-contact" : "platform-on-duty",
        suppressionMinutes: Number(draft.suppressionMinutes),
        status: "active",
        reason: `为${selectedTenant?.name ?? data.dashboard.tenantName}配置${items.find((item) => item.meterId === draft.meterId)?.displayName ?? "用量项"}提醒策略`,
        idempotencyKey: internalRequestKey("usage-alert-policy"),
      })});
      setDraft(null);
      await remote.reload();
    } catch (failure) {
      setSubmitError(failure instanceof Error ? failure.message : "提醒策略保存失败");
    }
  }

  function prepareApprovalDraft() {
    if (!draft || !data) return;
    const item = items.find((candidate) => candidate.meterId === draft.meterId);
    if (!item) { setSubmitError("请先选择一个用量项。"); return; }
    if (draft.reason.trim().length < 12) { setSubmitError("请用业务语言说明原因，至少 12 个字。"); return; }
    const isAdjust = draft.kind === "adjust";
    const action: HighRiskAction = isAdjust ? "usage.adjust" : "overrides.manage";
    const targetLabel = `${selectedTenant?.name ?? data.dashboard.tenantName} · ${item.displayName}`;
    const payload = isAdjust
      ? {
        tenantId: data.selectedTenantId,
        meterId: item.meterId,
        periodStart: data.dashboard.periodStart,
        periodEnd: data.dashboard.periodEnd,
        quantityDelta: draft.quantityDelta.trim(),
      }
      : {
        tenantId: data.selectedTenantId,
        meterId: item.meterId,
        unlimited: false,
        limitQuantity: draft.limitQuantity.trim(),
        softLimitPercent: 80,
        startsAt: fromDateTimeLocal(draft.startsAt),
        endsAt: fromDateTimeLocal(draft.endsAt),
      };
    setDraft(null);
    setApproval({
      operation: { action, targetType: isAdjust ? "usage_adjustment" : "quota_override", targetId: item.meterId, tenantId: data.selectedTenantId, payload },
      title: isAdjust ? "提交用量修正审批" : "提交临时额度审批",
      targetLabel,
      executePath: isAdjust ? "/api/usage/adjustments" : "/api/usage/quota-overrides",
      body: isAdjust
        ? { ...payload, sourceIdempotencyKey: internalRequestKey("usage-adjust"), reason: draft.reason.trim(), idempotencyKey: internalRequestKey("usage-adjust-submit") }
        : { ...payload, reason: draft.reason.trim(), idempotencyKey: internalRequestKey("quota-override") },
    });
  }

  async function executeApproval(approvalId: string, reason: string) {
    if (!approval) return;
    await api(approval.executePath, { method: "POST", body: JSON.stringify({ ...approval.body, approvalId, reason }) });
    setApproval(null);
    await remote.reload();
  }

  async function toggleAlert(row: AlertPolicy) {
    if (!data) return;
    setSubmitError(null);
    try {
      await api("/api/usage/alert-policies", { method: "PUT", body: JSON.stringify({
        tenantId: data.selectedTenantId,
        meterId: row.meterId,
        thresholdPercent: row.thresholdPercent,
        channel: row.channel,
        recipientKind: row.recipientKind,
        recipientRef: row.recipientRef,
        suppressionMinutes: row.suppressionMinutes,
        status: row.status === "active" ? "paused" : "active",
        reason: `${row.status === "active" ? "暂停" : "恢复"}${row.meterName}的用量提醒策略`,
        idempotencyKey: internalRequestKey("usage-alert-status"),
      }) });
      await remote.reload();
    } catch (failure) {
      setSubmitError(failure instanceof Error ? failure.message : "提醒状态更新失败");
    }
  }

  const usageColumns: Array<DataTableColumn<UsageItem>> = [
    { key: "name", header: "用量项", render: (row) => <span><strong>{row.displayName}</strong><small>{formatLatency(row.dataLatencyMinutes)}</small></span> },
    { key: "trend", header: "本日 / 本月", render: (row) => row.dataAvailable ? <span><span className="app-number">{formatQuantity(row.todayQuantity, row.unit, row.decimalScale)}</span><small>本月 {formatQuantity(row.monthQuantity, row.unit, row.decimalScale)}</small></span> : <span>尚未接入<small>等待首次真实用量</small></span> },
    { key: "quota", header: "包含 / 已用 / 剩余", render: (row) => <span><strong className="app-number">{formatQuantity(row.includedQuantity, row.unit, row.decimalScale)}</strong><small>{row.dataAvailable ? `已用 ${formatQuantity(row.usedQuantity, row.unit, row.decimalScale)} · 剩余 ${formatQuantity(row.remainingQuantity, row.unit, row.decimalScale)}` : "已用与剩余等待首次真实用量"}</small></span> },
    { key: "forecast", header: "预计耗尽", render: (row) => !row.dataAvailable ? "等待首次真实用量" : row.unlimited ? "不限量" : row.forecastExhaustedAt ? formatDate(row.forecastExhaustedAt) : "数据不足，暂不预测" },
    { key: "policy", header: "超额处理", render: (row) => overagePolicyText(row.overagePolicy) },
    { key: "updated", header: "更新时间", render: (row) => row.dataAvailable ? formatDateTime(row.updatedAt) : "尚未更新" },
  ];
  const alertColumns: Array<DataTableColumn<AlertPolicy>> = [
    { key: "meter", header: "提醒对象", render: (row) => row.meterName },
    { key: "threshold", header: "提醒线", render: (row) => <span className="app-number">{row.thresholdPercent}%</span> },
    { key: "recipient", header: "通知对象", render: (row) => recipientText(row.recipientKind) },
    { key: "channel", header: "通知方式", render: (row) => ({ internal: "站内提醒", email: "邮件提醒", sms: "短信提醒" } as Record<string, string>)[row.channel] ?? "待确认" },
    { key: "window", header: "重复提醒间隔", render: (row) => row.suppressionMinutes >= 60 ? `${Math.round(row.suppressionMinutes / 60)} 小时` : `${row.suppressionMinutes} 分钟` },
    { key: "status", header: "状态", render: (row) => <StatusBadge value={row.status} /> },
    { key: "updated", header: "更新时间", render: (row) => formatDateTime(row.updatedAt) },
  ];
  const meterColumns: Array<DataTableColumn<MeterCatalogItem>> = [
    { key: "name", header: "计量规则", render: (row) => <span><strong>{row.displayName}</strong><small>{row.capabilityName ? `关联功能：${row.capabilityName}` : "独立用量，不限制具体功能"}</small></span> },
    { key: "unit", header: "单位 / 精度", render: (row) => <span><strong>{row.unit}</strong><small>{row.decimalScale === 0 ? "按整数记录" : `保留 ${row.decimalScale} 位小数`}</small></span> },
    { key: "aggregation", header: "怎么汇总", render: (row) => aggregationText(row.aggregation) },
    { key: "source", header: "来源 / 防重复", render: (row) => <span>{sourceText(row.source)}<small>{row.dedupeKeyRequired ? "按来源业务记录防重复" : "仅防重复提交"}</small></span> },
    { key: "billing", header: "计费方式", render: (row) => row.billable ? "可进入套餐计费" : "只统计，不收费" },
    { key: "latency", header: "更新速度", render: (row) => formatLatency(row.dataLatencyMinutes) },
    { key: "status", header: "状态", render: (row) => <StatusBadge value={row.lifecycle} /> },
  ];
  const overrideColumns: Array<DataTableColumn<QuotaOverride>> = [
    { key: "meter", header: "用量项", render: (row) => row.meterName },
    { key: "limit", header: "临时额度", render: (row) => <span className="app-number">{row.unlimited ? "不限量" : formatQuantity(row.limitQuantity, row.unit, row.decimalScale)}</span> },
    { key: "window", header: "有效时间", render: (row) => <span>{formatDateTime(row.startsAt)}<small>至 {formatDateTime(row.endsAt)}</small></span> },
    { key: "reason", header: "调整原因", render: (row) => row.reason },
    { key: "evidence", header: "审批依据", render: (row) => row.approvalId ? "审批已留痕" : "历史记录待复核" },
    { key: "status", header: "当前状态", render: (row) => <StatusBadge value={row.releasedAt ? "revoked" : new Date(row.endsAt) < new Date() ? "expired" : new Date(row.startsAt) > new Date() ? "pending" : "active"} /> },
  ];
  const adjustmentColumns: Array<DataTableColumn<UsageAdjustment>> = [
    { key: "meter", header: "用量项", render: (row) => row.meterName },
    { key: "quantity", header: "修正数量", render: (row) => <strong className="app-number">{row.quantityDelta.startsWith("-") ? formatQuantity(row.quantityDelta, row.unit, row.decimalScale) : `+${formatQuantity(row.quantityDelta, row.unit, row.decimalScale)}`}</strong> },
    { key: "period", header: "所属账期", render: (row) => `${formatDate(row.periodStart)} - ${formatDate(row.periodEnd)}` },
    { key: "reason", header: "修正原因", render: (row) => row.reason },
    { key: "evidence", header: "审批依据", render: (row) => row.approvalId ? "审批已留痕" : "历史记录待复核" },
    { key: "created", header: "记录时间", render: (row) => formatDateTime(row.createdAt) },
  ];

  return <div className="page usage-page">
    <PageHeader
      icon={Gauge}
      title="用量与额度"
      meta={selectedTenant ? `${selectedTenant.name} · ${periodText}` : `当前账期：${periodText}`}
      actions={<div className="inline-actions">
        {(data?.tenants.length ?? 0) > 0 && <TenantPicker tenants={data?.tenants ?? []} value={data?.selectedTenantId ?? selectedTenantId} onChange={setSelectedTenantId} />}
        <button className="button secondary" onClick={() => void remote.reload()}><RefreshCw size={16} />刷新</button>
      </div>}
      navigation={<V3PageTabs
        active={activeView}
        onChange={(value) => setActiveView(value as typeof activeView)}
        ariaLabel="用量与额度视图"
        tabs={[
          { value: "usage", label: "用量概览", icon: Gauge, count: data?.dashboard.items.length ?? 0 },
          { value: "policy", label: "提醒与额度", icon: BellRing, count: activePolicyCount + activeOverrideCount },
          { value: "records", label: "修正记录", icon: History, count: data?.dashboard.adjustments.length ?? 0 },
          { value: "meters", label: "计量规则", icon: BookOpenCheck, count: data?.meters.length ?? 0 },
        ]}
      />}
    />
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={blocked}
      emptyHint="还没有可管理的企业。先完成租户开通后，再回来查看用量与额度。"
    >
      {(ready) => <div className="workspace-stack usage-workspace">
        <section className="usage-scope-summary" aria-label="当前企业用量摘要">
          <div className="usage-scope-identity"><span><Building2 size={18} aria-hidden /></span><div><small>当前企业</small><strong>{ready.dashboard.tenantName}</strong></div></div>
          <dl>
            <div><dt><CalendarClock size={14} />当前账期</dt><dd className="app-number">{formatDate(ready.dashboard.periodStart)} - {formatDate(ready.dashboard.periodEnd)}</dd></div>
            <div><dt><Activity size={14} />今日发生用量</dt><dd><strong className="app-number">{ready.dashboard.items.filter((item) => item.dataAvailable && item.todayQuantity !== "0").length}</strong> 项</dd></div>
            <div><dt><TrendingUp size={14} />本月有记录</dt><dd><strong className="app-number">{ready.dashboard.items.filter((item) => item.dataAvailable && item.monthQuantity !== "0").length}</strong> 项</dd></div>
            <div><dt><BellRing size={14} />生效提醒</dt><dd><strong className="app-number">{ready.alertPolicies.filter((item) => item.status === "active").length}</strong> 条</dd></div>
          </dl>
        </section>

        {activeView === "usage" && <SectionCard
          icon={Gauge}
          title="套餐用量台账"
          meta={`${ready.dashboard.items.length} 项`}
          variant="ledger"
          filters={<div className="inline-actions">
            {canManageAlerts && <button className="button secondary" disabled={!items.length} onClick={() => openDraft("alert")}><BellRing size={15} />新增提醒</button>}
            {canOverride && <button className="button secondary" disabled={!items.length} onClick={() => openDraft("override")}><SlidersHorizontal size={15} />临时额度</button>}
            {canAdjust && <button className="button primary" disabled={!items.length} onClick={() => openDraft("adjust")}><Plus size={15} />用量修正</button>}
          </div>}
        >
          <DataTable columns={usageColumns} rows={ready.dashboard.items} rowKey={(row) => row.meterId} ariaLabel="套餐用量台账" empty={<span>当前企业还没有接入可计量的套餐用量。</span>} />
        </SectionCard>}

        {activeView === "policy" && <div className="usage-view-stack"><SectionCard icon={BellRing} title="提醒策略" meta={`${ready.alertPolicies.length} 条`} variant="ledger" filters={canManageAlerts ? <button className="button primary" disabled={!items.length} onClick={() => openDraft("alert")}><Plus size={15} />新增提醒</button> : undefined}>
          <DataTable
            columns={alertColumns}
            rows={ready.alertPolicies}
            rowKey={(row) => row.id}
            ariaLabel="用量提醒策略"
            {...(canManageAlerts ? { rowActions: (row: AlertPolicy) => <button className="button ghost" onClick={() => void toggleAlert(row)}>{row.status === "active" ? "暂停" : "启用"}</button> } : {})}
            empty={<span>还没有提醒策略。可按 50%、80%、100% 配置给账务联系人提醒。</span>}
          />
        </SectionCard>

        <SectionCard icon={SlidersHorizontal} title="临时额度" meta={`${ready.dashboard.quotaOverrides.length} 条`} variant="ledger" filters={canOverride ? <button className="button secondary" disabled={!items.length} onClick={() => openDraft("override")}><Plus size={15} />申请临时额度</button> : undefined}>
          <DataTable columns={overrideColumns} rows={ready.dashboard.quotaOverrides} rowKey={(row) => row.id} ariaLabel="临时额度记录" empty={<span>当前企业没有临时额度。需要短期扩容时，提交审批并设置自动失效时间。</span>} />
        </SectionCard></div>}

        {activeView === "records" && <SectionCard icon={History} title="用量修正记录" meta={`${ready.dashboard.adjustments.length} 条`} variant="ledger" filters={canAdjust ? <button className="button primary" disabled={!items.length} onClick={() => openDraft("adjust")}><Plus size={15} />申请用量修正</button> : undefined}>
          <DataTable columns={adjustmentColumns} rows={ready.dashboard.adjustments} rowKey={(row) => row.id} ariaLabel="用量修正记录" empty={<span>当前账期没有修正记录。迟到或更正的用量会追加留痕，不会覆盖原记录。</span>} />
        </SectionCard>}

        {activeView === "meters" && <SectionCard
          icon={BookOpenCheck}
          title="计量规则目录"
          meta={`${ready.meters.length} 项`}
          variant="ledger"
          filters={canManageMeters ? <div className="inline-actions"><button className="button primary" onClick={() => openMeterDraft()}><Plus size={15} />新增计量规则</button></div> : undefined}
        >
          <DataTable
            columns={meterColumns}
            rows={ready.meters}
            rowKey={(row) => row.id}
            ariaLabel="计量规则目录"
            {...(canManageMeters ? { rowActions: (row: MeterCatalogItem) => <button className="button ghost" onClick={() => openMeterDraft(row)}>编辑</button> } : {})}
            empty={<span>还没有计量规则。先建立计量名称、单位、汇总方式和数据来源，再把它加入套餐。</span>}
          />
        </SectionCard>}
      </div>}
    </RemotePageState>
    {draft && <UsageDraftWizard
      draft={draft}
      setDraft={setDraft}
      meterOptions={meterOptions}
      submitError={submitError}
      onClose={() => setDraft(null)}
      onSubmit={draft.kind === "alert" ? submitAlert : prepareApprovalDraft}
    />}
    {meterDraft && <MeterWizard
      draft={meterDraft}
      setDraft={setMeterDraft}
      capabilityOptions={capabilityOptions}
      submitError={submitError}
      onClose={() => setMeterDraft(null)}
      onSubmit={submitMeter}
    />}
    {approval && session && <ApprovalGateModal
      session={session}
      operation={approval.operation}
      title={approval.title}
      targetLabel={approval.targetLabel}
      impact={approval.operation.action === "usage.adjust"
        ? ["会追加一条修正记录，不覆盖原始用量。", "会影响当前账期剩余额度和账单复核。"]
        : ["会在有效期内改变当前企业的可用额度。", "到期后自动回到套餐版本规定。"]}
      recovery="如发现不符合业务事实，可再次发起反向修正或等待临时额度到期。"
      execute={executeApproval}
      onClose={() => setApproval(null)}
      onExecuted={() => void remote.reload()}
    />}
    {approval && !session && <InlineFeedback tone="danger">高风险操作需要登录会话，请刷新后重试。</InlineFeedback>}
  </div>;
}

function TenantPicker({ tenants, value, onChange }: { tenants: TenantOption[]; value: string; onChange: (tenantId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = tenants.find((tenant) => tenant.id === value) ?? tenants[0] ?? null;
  const normalized = query.trim().toLocaleLowerCase("zh-CN");
  const matches = tenants.filter((tenant) => !normalized || tenant.name.toLocaleLowerCase("zh-CN").includes(normalized));
  const visible = matches.slice(0, 30);

  function selectTenant(tenantId: string) {
    onChange(tenantId);
    setQuery("");
    setOpen(false);
  }

  return <div className={`usage-tenant-picker ${open ? "open" : ""}`}>
    <button type="button" className="usage-tenant-trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
      <Building2 size={16} aria-hidden />
      <span><small>当前企业</small><strong>{selected?.name ?? "选择企业"}</strong></span>
      <ChevronDown size={15} aria-hidden />
    </button>
    {open && <div className="usage-tenant-popover">
      <label className="usage-tenant-search"><Search size={15} aria-hidden /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索企业名称" aria-label="搜索企业" /></label>
      <div className="usage-tenant-results" role="listbox" aria-label="企业列表">
        {visible.map((tenant) => <button key={tenant.id} type="button" role="option" aria-selected={tenant.id === selected?.id} onClick={() => selectTenant(tenant.id)}>
          <span><strong>{tenant.name}</strong><small>{tenant.status === "active" ? "运行中" : "需关注"}</small></span>
          {tenant.id === selected?.id && <Check size={16} aria-hidden />}
        </button>)}
        {matches.length === 0 && <p>没有找到匹配的企业</p>}
      </div>
      {matches.length > visible.length && <small className="usage-tenant-more">结果较多，请继续输入企业名称缩小范围</small>}
    </div>}
  </div>;
}

function UsageDraftWizard({ draft, setDraft, meterOptions, submitError, onClose, onSubmit }: {
  draft: DraftState;
  setDraft: (draft: DraftState) => void;
  meterOptions: Array<{ value: string; label: string }>;
  submitError: string | null;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const title = draft.kind === "alert" ? "新增用量提醒" : draft.kind === "adjust" ? "申请用量修正" : "申请临时额度";
  const submitLabel = draft.kind === "alert" ? "保存提醒策略" : "下一步：提交审批";
  const steps = [
    {
      key: "target",
      title: "选择对象",
      summary: "选择企业当前账期里的业务用量项，系统会自动关联对应记录。",
      content: <div className="form-grid">
        <label className="full field-block">用量项<V3Select value={draft.meterId} onChange={(meterId) => setDraft({ ...draft, meterId })} options={meterOptions} ariaLabel="选择用量项" /></label>
      </div>,
      validate: () => draft.meterId ? null : "请先选择用量项。",
    },
    {
      key: "content",
      title: draft.kind === "alert" ? "提醒规则" : "调整内容",
      summary: draft.kind === "alert" ? "提醒达到后会通知指定业务联系人，重复提醒会按间隔抑制。" : "高风险调整必须写清楚原因，并由另一名管理员批准后执行。",
      content: <div className="form-grid">
        {draft.kind === "alert" ? <>
          <label>提醒线（%）<input value={draft.thresholdPercent} onChange={(event) => setDraft({ ...draft, thresholdPercent: event.target.value })} placeholder="例如 80" /></label>
          <label>通知方式<V3Select value={draft.channel} onChange={(channel) => setDraft({ ...draft, channel })} ariaLabel="通知方式" options={[{ value: "internal", label: "站内提醒" }, { value: "email", label: "邮件提醒" }, { value: "sms", label: "短信提醒" }]} /></label>
          <label>通知对象<V3Select value={draft.recipientKind} onChange={(recipientKind) => setDraft({ ...draft, recipientKind: recipientKind as DraftState["recipientKind"] })} ariaLabel="通知对象" options={[{ value: "billing_profile", label: "账单联系人" }, { value: "tenant_contact", label: "企业主要联系人" }, { value: "admin", label: "平台值班管理员" }]} /></label>
          <label>重复提醒间隔（分钟）<input value={draft.suppressionMinutes} onChange={(event) => setDraft({ ...draft, suppressionMinutes: event.target.value })} placeholder="例如 1440" /></label>
        </> : draft.kind === "adjust" ? <>
          <label>修正数量<input value={draft.quantityDelta} onChange={(event) => setDraft({ ...draft, quantityDelta: event.target.value })} placeholder="例如 10 或 -10" /></label>
          <label className="full">修正原因<textarea rows={3} value={draft.reason} onChange={(event) => setDraft({ ...draft, reason: event.target.value })} placeholder="说明来源单据、复核人和本次修正原因" /></label>
        </> : <>
          <label>临时额度<input value={draft.limitQuantity} onChange={(event) => setDraft({ ...draft, limitQuantity: event.target.value })} placeholder="例如 1000" /></label>
          <label>开始时间<input type="datetime-local" value={draft.startsAt} onChange={(event) => setDraft({ ...draft, startsAt: event.target.value })} /></label>
          <label>失效时间<input type="datetime-local" value={draft.endsAt} onChange={(event) => setDraft({ ...draft, endsAt: event.target.value })} /></label>
          <label className="full">调整原因<textarea rows={3} value={draft.reason} onChange={(event) => setDraft({ ...draft, reason: event.target.value })} placeholder="说明客户场景、有效期依据和审批人确认口径" /></label>
        </>}
      </div>,
      validate: () => {
        if (draft.kind === "alert") {
          const threshold = Number(draft.thresholdPercent);
          const suppression = Number(draft.suppressionMinutes);
          if (!Number.isInteger(threshold) || threshold <= 0 || threshold > 100) return "提醒线必须是 1 到 100 的整数。";
          if (!Number.isInteger(suppression) || suppression < 0) return "重复提醒间隔必须是非负整数。";
          return null;
        }
        if (draft.kind === "adjust" && !/^-?[1-9][0-9]{0,17}$/.test(draft.quantityDelta.trim())) return "修正数量必须是非零整数。";
        if (draft.kind === "override") {
          if (!/^[1-9][0-9]{0,17}$/.test(draft.limitQuantity.trim())) return "临时额度必须是正整数。";
          if (new Date(draft.endsAt).getTime() <= new Date(draft.startsAt).getTime()) return "失效时间必须晚于开始时间。";
        }
        return draft.reason.trim().length >= 12 ? null : "请用业务语言说明原因，至少 12 个字。";
      },
    },
    {
      key: "confirm",
      title: "确认提交",
      summary: draft.kind === "alert" ? "保存后将按当前策略提醒，不影响客户实际额度。" : "提交后先进入审批，不会直接改变客户用量或额度。",
      content: <div className="confirm-summary">
        <strong>{title}</strong>
        <p>{draft.kind === "alert" ? "达到提醒线后通知业务联系人。" : "审批通过并执行后，页面会重新读取真实结果。"}</p>
      </div>,
    },
  ];
  return <Wizard title={title} description="按业务口径填写，提交后会进入审计留痕。" steps={steps} submitLabel={submitLabel} onSubmit={onSubmit} onClose={onClose} submitError={submitError} />;
}

export function MeterWizard({ draft, setDraft, capabilityOptions, submitError, onClose, onSubmit }: {
  draft: MeterDraftState;
  setDraft: (draft: MeterDraftState) => void;
  capabilityOptions: Array<{ value: string; label: string; hint?: string }>;
  submitError: string | null;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const steps = [
    {
      key: "meaning",
      title: "这项用量是什么",
      summary: "名称和单位会直接展示给总管和客户，请使用业务人员熟悉的叫法。",
      content: <div className="form-grid">
        <label>计量名称<input value={draft.displayName} onChange={(event) => setDraft({ ...draft, displayName: event.target.value })} placeholder="例如 订单处理量" /></label>
        <label>计量单位<input value={draft.unit} onChange={(event) => setDraft({ ...draft, unit: event.target.value })} placeholder="例如 单、次、GB" /></label>
        <label className="full field-block">关联功能<V3Select value={draft.capabilityId} onChange={(capabilityId) => setDraft({ ...draft, capabilityId })} options={capabilityOptions} ariaLabel="关联功能" /></label>
      </div>,
      validate: () => draft.displayName.trim().length >= 2 && draft.unit.trim() ? null : "请填写至少 2 个字的计量名称和清楚的单位。",
    },
    {
      key: "rule",
      title: "系统怎样计算",
      summary: "按真实业务口径选择汇总和防重复方式，保存后租户 ERP 必须按同一规则上报。",
      content: <div className="form-grid">
        <label>汇总方式<V3Select value={draft.aggregation} onChange={(aggregation) => setDraft({ ...draft, aggregation: aggregation as MeterCatalogItem["aggregation"] })} ariaLabel="汇总方式" options={[
          { value: "sum", label: "累计相加", hint: "适合订单数、调用次数、流量" },
          { value: "max", label: "取最高值", hint: "适合峰值席位、最大并发" },
          { value: "last", label: "取最新值", hint: "适合期末容量、当前占用" },
          { value: "unique_count", label: "按业务记录去重计数", hint: "适合独立订单、客户、商品" },
        ]} /></label>
        <label>数据来源<V3Select value={draft.source} onChange={(source) => setDraft({ ...draft, source: source as MeterCatalogItem["source"] })} ariaLabel="数据来源" options={[{ value: "erp_runtime", label: "租户 ERP 上报" }, { value: "control_plane", label: "总管后台记录" }]} /></label>
        <label>小数精度<V3Select value={draft.decimalScale} onChange={(decimalScale) => setDraft({ ...draft, decimalScale })} ariaLabel="小数精度" options={[0, 1, 2, 3, 4, 5, 6].map((value) => ({ value: String(value), label: value === 0 ? "只记录整数" : `保留 ${value} 位小数` }))} /></label>
        <label>通常多久更新<V3Select value={draft.dataLatencyMinutes} onChange={(dataLatencyMinutes) => setDraft({ ...draft, dataLatencyMinutes })} ariaLabel="更新速度" options={[{ value: "0", label: "实时" }, { value: "5", label: "约 5 分钟" }, { value: "15", label: "约 15 分钟" }, { value: "60", label: "约 1 小时" }, { value: "1440", label: "每天一次" }]} /></label>
        <label>防重复方式<V3Select value={draft.dedupeKeyRequired ? "business" : "request"} onChange={(value) => setDraft({ ...draft, dedupeKeyRequired: value === "business" })} ariaLabel="防重复方式" options={[{ value: "business", label: "按来源业务记录防重复" }, { value: "request", label: "只防重复提交" }]} /></label>
        <label>是否进入收费<V3Select value={draft.billable ? "billable" : "tracking"} onChange={(value) => setDraft({ ...draft, billable: value === "billable" })} ariaLabel="是否进入收费" options={[{ value: "tracking", label: "只统计，不收费" }, { value: "billable", label: "可进入套餐计费" }]} /></label>
      </div>,
      validate: () => null,
    },
    {
      key: "publish",
      title: "保存与启用",
      summary: "草稿不会接收正式用量；启用后才可加入套餐并接收上报。停用旧规则不会删除历史记录。",
      content: <div className="form-grid">
        <label>保存状态<V3Select value={draft.lifecycle} onChange={(lifecycle) => setDraft({ ...draft, lifecycle: lifecycle as MeterCatalogItem["lifecycle"] })} ariaLabel="保存状态" options={[{ value: "draft", label: "先保存草稿" }, { value: "active", label: "保存并启用" }, { value: "retired", label: "停止接收新用量" }]} /></label>
        <label className="full">变更原因<textarea rows={3} value={draft.reason} onChange={(event) => setDraft({ ...draft, reason: event.target.value })} placeholder="说明为什么新增或调整这项计量规则" /></label>
      </div>,
      validate: () => draft.reason.trim().length >= 8 ? null : "请用业务语言说明变更原因，至少 8 个字。",
    },
    {
      key: "confirm",
      title: "确认保存",
      summary: "保存后会写入审计记录；内部编号由系统生成，不需要手工填写。",
      content: <div className="confirm-summary"><strong>{draft.displayName || "新计量规则"}</strong><p>{aggregationText(draft.aggregation)} · {sourceText(draft.source)} · {draft.billable ? "可进入套餐计费" : "只统计，不收费"}</p></div>,
    },
  ];
  return <Wizard title={draft.displayName ? `维护${draft.displayName}` : "新增计量规则"} description="按业务含义完成设置，系统负责内部编号和审计留痕。" steps={steps} submitLabel="保存计量规则" onSubmit={onSubmit} onClose={onClose} submitError={submitError} />;
}
